import { useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check } from 'lucide-react';
import { toast } from 'sonner';
import { api, errorMessage } from '@/lib/api/client';
import type { Customer } from '@/lib/api/types';
import { Button } from '@/ui/Button';
import { Checkbox, Field, Input, MoneyInput, Textarea } from '@/ui/Field';
import { Page, PageBody, PageHeader, Panel } from '@/ui/Panel';

export const Route = createFileRoute('/_app/customers_/new')({
  staticData: { crumb: 'New customer' },
  component: NewCustomerScreen,
});

type Draft = {
  firstName: string;
  lastName: string;
  companyName: string;
  accountNumber: string;
  phone: string;
  email: string;
  hasCredit: boolean;
  creditLimit: number | null;
  pctDiscount: string;
  taxExempt: boolean;
  notes: string;
};

const EMPTY: Draft = {
  firstName: '',
  lastName: '',
  companyName: '',
  accountNumber: '',
  phone: '',
  email: '',
  hasCredit: false,
  creditLimit: null,
  pctDiscount: '',
  taxExempt: false,
  notes: '',
};

function NewCustomerScreen() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const discount = draft.pctDiscount.trim() === '' ? 0 : Number(draft.pctDiscount);
  const discountInvalid =
    draft.pctDiscount.trim() !== '' &&
    (!Number.isInteger(discount) || discount < 0 || discount > 100);
  const nameMissing = !draft.firstName.trim() || !draft.lastName.trim();

  const create = useMutation({
    mutationFn: () =>
      api.post<Customer>('/customers', {
        firstName: draft.firstName.trim(),
        lastName: draft.lastName.trim(),
        ...(draft.companyName.trim() ? { companyName: draft.companyName.trim() } : {}),
        ...(draft.accountNumber.trim()
          ? { accountNumber: draft.accountNumber.trim() }
          : {}),
        ...(draft.phone.trim() ? { phone: draft.phone.trim() } : {}),
        ...(draft.email.trim() ? { email: draft.email.trim() } : {}),
        hasCredit: draft.hasCredit,
        creditLimit: draft.creditLimit ?? 0,
        pctDiscount: discount,
        taxExempt: draft.taxExempt,
        ...(draft.notes.trim() ? { notes: draft.notes.trim() } : {}),
      }),
    onSuccess: (customer) => {
      void qc.invalidateQueries({ queryKey: ['customers'] });
      toast.success(`${customer.name} created.`);
      void navigate({
        to: '/customers/$id',
        params: { id: String(customer.id) },
        search: { page: 1 },
      });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Page>
      <PageHeader
        eyebrow="Customers"
        title="New customer"
        subtitle="Identity, contact and house-account setup"
        actions={
          <>
            <Button
              asChild
              variant="ghost"
              size="sm"
              icon={<ArrowLeft className="size-3.5" />}
            >
              <Link
                to="/customers"
                search={{ q: '', sort: 'balance', order: 'desc', page: 1 }}
              >
                Cancel
              </Link>
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={<Check className="size-3.5" />}
              loading={create.isPending}
              disabled={nameMissing || discountInvalid}
              onClick={() => create.mutate()}
            >
              Create customer
            </Button>
          </>
        }
      />
      <PageBody>
        <div className="mx-auto grid max-w-4xl gap-3 md:grid-cols-2">
          <Panel title="Identity">
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="First name" required htmlFor="firstName">
                  <Input
                    id="firstName"
                    autoFocus
                    value={draft.firstName}
                    invalid={!draft.firstName.trim() && create.isError}
                    onChange={(e) => set('firstName', e.currentTarget.value)}
                  />
                </Field>
                <Field label="Last name" required htmlFor="lastName">
                  <Input
                    id="lastName"
                    value={draft.lastName}
                    invalid={!draft.lastName.trim() && create.isError}
                    onChange={(e) => set('lastName', e.currentTarget.value)}
                  />
                </Field>
              </div>
              <Field label="Company" hint="Shown on receipts for wholesale accounts.">
                <Input
                  value={draft.companyName}
                  onChange={(e) => set('companyName', e.currentTarget.value)}
                />
              </Field>
              <Field label="Account number" hint="Must be unique. Leave blank to skip.">
                <Input
                  mono
                  value={draft.accountNumber}
                  onChange={(e) => set('accountNumber', e.currentTarget.value)}
                />
              </Field>
            </div>
          </Panel>

          <Panel title="Contact">
            <div className="flex flex-col gap-3">
              <Field
                label="Phone"
                hint="Becomes the primary contact number on the account."
              >
                <Input
                  mono
                  value={draft.phone}
                  onChange={(e) => set('phone', e.currentTarget.value)}
                  placeholder="(555) 123-4567"
                />
              </Field>
              <Field label="Email">
                <Input
                  type="email"
                  value={draft.email}
                  onChange={(e) => set('email', e.currentTarget.value)}
                />
              </Field>
              <Field label="Notes">
                <Textarea
                  rows={4}
                  value={draft.notes}
                  onChange={(e) => set('notes', e.currentTarget.value)}
                  placeholder="Anything a cashier should know at the register."
                />
              </Field>
            </div>
          </Panel>

          <Panel
            title="House account"
            description="Charging on account starts at a zero balance."
            className="md:col-span-2"
          >
            <div className="flex flex-col gap-3">
              <Checkbox
                checked={draft.hasCredit}
                onCheckedChange={(checked) => set('hasCredit', checked)}
                label="Allow charging to a house account"
              />
              <div className="grid gap-3 sm:grid-cols-3">
                <Field
                  label="Credit limit"
                  hint={
                    draft.hasCredit
                      ? 'Zero means no limit is enforced.'
                      : 'Credit is off.'
                  }
                >
                  <MoneyInput
                    value={draft.creditLimit}
                    onValueChange={(cents) => set('creditLimit', cents)}
                    disabled={!draft.hasCredit}
                  />
                </Field>
                <Field
                  label="Discount %"
                  error={discountInvalid ? 'Whole number, 0–100.' : null}
                  hint="Applied to every sale on this account."
                >
                  <Input
                    mono
                    inputMode="numeric"
                    value={draft.pctDiscount}
                    invalid={discountInvalid}
                    onChange={(e) => set('pctDiscount', e.currentTarget.value)}
                    placeholder="0"
                  />
                </Field>
                <Field label="Tax" hint="Exempt accounts are not charged sales tax.">
                  <div className="flex h-8 items-center">
                    <Checkbox
                      checked={draft.taxExempt}
                      onCheckedChange={(checked) => set('taxExempt', checked)}
                      label="Tax exempt"
                    />
                  </div>
                </Field>
              </div>
            </div>
          </Panel>
        </div>
      </PageBody>
    </Page>
  );
}
